/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import AcClusterBadge from './AcClusterBadge';
import AcCustomChargerMarker from './AcCustomChargerMarker';
import './CustomNOCMap.css';
import { getacNocDashboardReducer, getAcFilterReducer } from '../../rmsReduxStore/acReduxExports';
import { useAppSelector } from '../../../globalUtils/globalExports';
import AcCustomChargerInfoCard from './AcCustomChargerInfoCard';
import { v4 } from 'uuid';


interface ChargerMarker extends google.maps.Marker {
  customData?: IChargerDetails;
}

interface Cluster {
  markers: google.maps.Marker[];
  position: google.maps.LatLng;
  count: number;
  clusterId: string;
}

interface ICustomNOCMAPProps {
  chargers: IChargerDetails[] | null;
  center: any;
  zoom: any;
  enableUserInteraction: boolean;
  handleSelectedChargerChange: (chargerName: string) => void;
  selectedCharger: string | null;
  darkModeEnable?: boolean;
  prevCenter: any;
}

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY ?? '';

const GOOGLE_MAP_LIBRARIES: ('marker')[] = ['marker'];

const AcCustomNOCMap: React.FC<ICustomNOCMAPProps> = (props) => {
  const { chargers, center, zoom, enableUserInteraction, handleSelectedChargerChange, selectedCharger, darkModeEnable, prevCenter } = props;
  const { prevMapCenter, setPrevMapCenter } = prevCenter;
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);
  const { nocDashboardLatestAlarmCharger, nocDashboardMapCenterCoordinates } = useAppSelector(getacNocDashboardReducer);
  const [showDetailsCard, setShowDetailsCard] = useState<string | null>(nocDashboardLatestAlarmCharger?.chargerId ? nocDashboardLatestAlarmCharger?.chargerId : null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const clusteringEndListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const [clustersForRender, setClustersForRender] = useState<Cluster[]>([]);
  const { mapCenter, setMapCenter } = center;
  const { mapZoom, setMapZoom } = zoom;
  const screenFilters = useAppSelector(getAcFilterReducer);
  const [showChargingStatus, setShowChargingStatus] = useState<boolean>(false);
  const [hideClusters, setHideClusters] = useState<boolean>(false);

  const mapStyle = [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'transit',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'administrative.neighborhood',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
  ];

  const darkModeStyle = [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'transit',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'off' }]
    },
    { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8f8f8f' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1d1d1d' }] },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#2a2a2a' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#1a1a1a' }]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#7a7a7a' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#3a3a3a' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#2a2a2a' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#0f0f0f' }]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#6e6e6e' }]
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#b3b3b3' }]
    }
  ];

  const markersData = useMemo(() => {
    if (!chargers || chargers.length === 0) return [];

    const spacingMeters = 6;
    const maxRadiusMeters = 30;
    const goldenAngle = 137.50776405003785 * (Math.PI / 180);

    const metersToLatLng = (lat: number, dxMeters: number, dyMeters: number) => {
      const metersPerDegLat = 111320;
      const metersPerDegLng = Math.abs(Math.cos(lat * Math.PI / 180) * 111320);
      const dLat = dyMeters / metersPerDegLat;
      const dLng = dxMeters / (metersPerDegLng || 1);
      return { dLat, dLng };
    };

    const groups = new Map<string, IChargerDetails[]>();
    chargers?.forEach((c) => {
      const key = `${c?.lat?.toFixed(6)}|${c?.lon?.toFixed(6)}`;
      const arr = groups.get(key) ?? [];
      arr.push(c);
      groups.set(key, arr);
    });

    const result: {
      id: string;
      name: string;
      position: { lat: number; lng: number };
      chargerData: IChargerDetails;
    }[] = [];

    groups?.forEach((group) => {
      if (group.length === 1) {
        const c = group?.[0];
        result.push({
          id: c?.id,
          name: c?.name,
          position: { lat: c?.lat, lng: c?.lon },
          chargerData: c
        });
        return;
      }

      const ordered = [...group].sort((a, b) => (a?.id > b?.id ? 1 : -1));

      ordered?.forEach((c, idx) => {
        const i = idx;
        const radiusMeters = Math.min(maxRadiusMeters, spacingMeters * Math.sqrt(i));
        const angle = i * goldenAngle;
        const dx = radiusMeters * Math.cos(angle);
        const dy = radiusMeters * Math.sin(angle);
        const { dLat, dLng } = metersToLatLng(c?.lat, dx, dy);

        result.push({
          id: c?.id,
          name: c?.name,
          position: {
            lat: c?.lat + dLat,
            lng: c?.lon + dLng
          },
          chargerData: c
        });
      });
    });

    return result;
  }, [chargers]);

  function getBoundsZoomLevel(bounds, mapWidthPx, mapHeightPx) {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat: number) {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return radX2;
    }

    const ne = bounds?.getNorthEast();
    const sw = bounds?.getSouthWest();
    const latFraction = (latRad(ne?.lat()) - latRad(sw?.lat())) / Math.PI;
    const lngDiff = ne?.lng() - sw?.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    const latZoom = Math.floor(Math.log(mapHeightPx / (WORLD_DIM.height * latFraction)) / Math.LN2);
    const lngZoom = Math.floor(Math.log(mapWidthPx / (WORLD_DIM.width * lngFraction)) / Math.LN2);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  const autoZoom = useCallback(() => {
    if (!google?.maps) return;
    if (!mapRef.current) return;
    const bounds = new google.maps.LatLngBounds();
    const chargersWithLatLng = chargers?.filter(c => c?.lat && c?.lon);
    if (!chargersWithLatLng || chargersWithLatLng?.length === 0) return;
    chargersWithLatLng?.forEach(c => bounds?.extend({ lat: c?.lat, lng: c?.lon }));

    const mapDiv: HTMLDivElement = document.querySelector('.noc-dashboard-live-topology-map-container') as HTMLDivElement;
    if (!mapDiv) return;
    if (bounds.isEmpty()) return;
    const zoom = getBoundsZoomLevel(bounds, mapDiv?.offsetWidth, mapDiv?.offsetHeight);

    setMapZoom(Math.floor(Math.max(zoom, 5)));
  }, [chargers]);

  const onMapLoad = useCallback(async (map: google.maps.Map) => {
    mapRef.current = map;

    const markers: ChargerMarker[] | undefined = markersData
      ?.filter(marker => marker?.position?.lat != null && marker?.position?.lng != null)
      ?.map(marker => {
        const gMarker = new google.maps.Marker({
          position: marker?.position,
          map,
          title: marker?.name,
          visible: false
        }) as ChargerMarker;

        gMarker.customData = marker?.chargerData;
        return gMarker;
      });

    const invisibleRenderer = {
      render({ position }: { position: google.maps.LatLng }) {
        return new google.maps.Marker({
          position,
          opacity: 0,
          clickable: false,
          zIndex: -9999
        });
      }
    };

    const markerClusterer = new MarkerClusterer({
      map,
      markers,
      renderer: invisibleRenderer
    });

    clustererRef.current = markerClusterer;

    const updateClusters = () => {
      if (!markerClusterer) return;

      if (clusteringEndListenerRef.current) {
        clusteringEndListenerRef.current.remove();
      }

      clusteringEndListenerRef.current = google.maps.event.addListener(
        markerClusterer,
        'clusteringend',
        (clusterer: MarkerClusterer) => {
          const clusters: any[] = (clusterer as any).clusters || [];

          const formattedClusters: Cluster[] = clusters.map(cluster => ({
            markers: cluster.markers || [],
            position: cluster.position || new google.maps.LatLng(0, 0),
            count: cluster.count || (cluster.markers || []).length,
            clusterId: v4()
          }));
          setClustersForRender(formattedClusters);
        }
      );
    };

    map.addListener('idle', () => {
      updateClusters();
      const newMapCenter = map.getCenter();
      const center = { lat: newMapCenter?.lat(), lng: newMapCenter?.lng() };
      const newMapZoom = map.getZoom();
      setMapCenter(center);
      setMapZoom(newMapZoom);
    });

    setTimeout(updateClusters, 100);
  }, [markersData]);

  useEffect(() => {
    return () => {
      if (clusteringEndListenerRef.current) {
        clusteringEndListenerRef.current.remove();
      }
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !clustererRef.current) return;

    const map = mapRef.current;

    clustererRef.current.clearMarkers();

    const newMarkers: ChargerMarker[] = markersData
      ?.filter(marker => marker?.position?.lat != null && marker?.position?.lng != null)
      ?.map(marker => {
        const gMarker = new google.maps.Marker({
          position: marker.position,
          map,
          title: marker.name,
          visible: false
        }) as ChargerMarker;

        gMarker.customData = marker.chargerData;
        return gMarker;
      }) || [];

    clustererRef.current.addMarkers(newMarkers, true);

    setTimeout(() => {
      if (clustererRef.current) {
        clustererRef.current.render();
        if (nocDashboardMapCenterCoordinates?.lat && nocDashboardMapCenterCoordinates?.lon && prevMapCenter?.lat !== nocDashboardMapCenterCoordinates?.lat && prevMapCenter?.lng !== nocDashboardMapCenterCoordinates?.lon) {
          setMapCenter({ lat: nocDashboardMapCenterCoordinates?.lat, lng: nocDashboardMapCenterCoordinates?.lon });
          setPrevMapCenter({ lat: nocDashboardMapCenterCoordinates?.lat, lng: nocDashboardMapCenterCoordinates?.lon });
          autoZoom();
        }
      }
    }, 50);

  }, [markersData, hideClusters, nocDashboardMapCenterCoordinates]);

  useEffect(() => {
    const nocMapScreenFilters = screenFilters?.screenFilters?.filter(item => item.screen === 'nocDashboard')?.[0]?.filters;
    const defaultViewFilterValues = nocMapScreenFilters?.[0]?.value;
    const showChargingIcon = defaultViewFilterValues?.some(item => item?.id === 'chargingSessions') || false;
    setShowChargingStatus(showChargingIcon);
    const hideClusters = defaultViewFilterValues?.some(item => item?.id === 'disableCluster') || false;
    setHideClusters(hideClusters);
  }, [screenFilters]);

  const handleMarkerClicked = (charger) => {
    const id = charger.id;
    setShowDetailsCard(prev => prev === id ? null : id);
    handleSelectedChargerChange(id);
  };

  const handletoggle = (clusterId) => {
    setOpenClusterId(prev => prev === clusterId ? null : clusterId);
    setShowDetailsCard(null);
  };

  if (!isLoaded) {
    return <div style={{ padding: '20px' }}>Loading map...</div>;
  }

  return (
    <div className='noc-dashboard-live-topology-map-wrap'>
      <div className='noc-dashboard-live-topology-map-container'>
        <GoogleMap
          onLoad={onMapLoad}
          center={mapCenter}
          zoom={mapZoom}
          mapContainerStyle={{ width: '100%', height: '100%' }}
          options={{
            styles: darkModeEnable ? darkModeStyle : mapStyle,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            disableDoubleClickZoom: !enableUserInteraction,
            draggable: enableUserInteraction,
            draggableCursor: 'default',
            draggingCursor: 'move'
          }}
        >
          {clustersForRender.map((cluster, idx) => {
            const chargerData: IChargerDetails[] = cluster.markers
              .map((marker: ChargerMarker) => marker.customData)
              .filter((data): data is IChargerDetails => data !== undefined);

            if (hideClusters) {
              return chargerData.map(charger => (
                <OverlayView
                  key={`charger-${charger.id}`}
                  position={{ lat: charger.lat, lng: charger.lon }}
                  mapPaneName={OverlayView.FLOAT_PANE}
                >
                  <>
                    <AcCustomChargerMarker
                      charger={charger}
                      selectedCharger={selectedCharger}
                      showChargingIcon={showChargingStatus}
                      handleMarkerClicked={handleMarkerClicked}
                    />
                    {(showDetailsCard === charger.id) && (
                      <div className="noc-dashboard-marker-details-card">
                        <AcCustomChargerInfoCard charger={charger} handleMarkerClicked={handleMarkerClicked} />
                      </div>
                    )}
                  </>
                </OverlayView>
              ));
            }
            else if (chargerData.length === 1) {
              return (
                <OverlayView
                  key={`charger-${chargerData[0].id}`}
                  position={cluster.position}
                  mapPaneName={OverlayView.FLOAT_PANE}
                >
                  <>
                    <AcCustomChargerMarker charger={chargerData[0]} selectedCharger={selectedCharger} showChargingIcon={showChargingStatus} handleMarkerClicked={handleMarkerClicked} />
                    {
                      (showDetailsCard === chargerData?.[0]?.id) &&
                      <div className='noc-dashboard-marker-details-card'>
                        <AcCustomChargerInfoCard charger={chargerData[0]} handleMarkerClicked={handleMarkerClicked} />
                      </div>
                    }
                  </>
                </OverlayView>
              );
            }
            return (
              <OverlayView
                key={`cluster-${idx}-${cluster.count}`}
                position={cluster.position}
                mapPaneName={openClusterId === cluster.clusterId ? OverlayView.FLOAT_PANE : OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <AcClusterBadge
                  chargers={chargerData}
                  showChargingIcon={showChargingStatus}
                  clusterId={cluster.clusterId}
                  openClusterId={openClusterId}
                  onToggle={(clusterid) => handletoggle(clusterid)} />
              </OverlayView>
            );
          })}
        </GoogleMap>
      </div>
    </div>
  );
};

export default React.memo(AcCustomNOCMap, (prevProps, nextProps) => {
  if (prevProps?.chargers?.length !== nextProps?.chargers?.length) return false;
  if (prevProps?.center?.mapCenter?.lat !== nextProps?.center?.mapCenter?.lat) return false;
  if (prevProps?.center?.mapCenter?.lng !== nextProps?.center?.mapCenter?.lng) return false;
  if (prevProps?.zoom?.mapZoom !== nextProps?.zoom?.mapZoom) return false;
  if (prevProps?.enableUserInteraction !== nextProps?.enableUserInteraction) return false;
  if (prevProps?.selectedCharger !== nextProps?.selectedCharger) return false;

  const prevIds = prevProps?.chargers?.map(c => c.id).sort().join(',');
  const nextIds = nextProps?.chargers?.map(c => c.id).sort().join(',');

  return prevIds === nextIds;
});
